import { DisplayActivityFeed, DisplayLeaderboard, DisplaySlideshow } from '@/components/display';
import {
  displayVars,
  GRID_TEXTURE,
  pointsLabel,
  type DisplayTone,
} from '@/components/display/displayTheme';
import { useDisplaySocket } from '@/hooks/useDisplaySocket';
import {
  DISPLAY_TOKEN_PARAM,
  displayPollIntervals,
  withDisplayToken,
} from '@/lib/displayToken';
import { RequireOrganization, useApiClient } from '@startupquickstart/frontend';
import { useQuery } from '@tanstack/react-query';
import { Camera, LayoutDashboard, Moon, Sun } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

interface Team {
  id: string;
  eventId: string;
  name: string;
  code: string;
  emoji: string | null;
  iconName: string;
  iconColor: string;
  totalScore: number;
  challengesCompleted: number;
  memberCount: number;
  createdAt: string;
  updatedAt: string;
}

interface AllianceEntry {
  allianceId: string;
  allianceName: string;
  allianceColor: string | null;
  allianceIconName: string | null;
  allianceIconColor: string | null;
  totalPoints: number;
  challengesCompleted: number;
  teamCount: number;
  rank: number;
}

interface LeaderboardData {
  entries: unknown[];
  alliances: AllianceEntry[];
  eventId: string;
  leaderboardVisible: boolean;
  lastUpdated: string;
}

interface DisplayEvent {
  id: string;
  name: string;
  description?: string;
  status: string;
  logoUrl?: string | null;
  backgroundColor?: string | null;
  accentColor?: string | null;
  customPointsLabel?: string | null;
}

interface ActivityItem {
  id: string;
  type: string;
  message: string;
  teamId: string | null;
  teamName: string | null;
  teamEmoji?: string | null;
  teamIconName?: string;
  teamIconColor?: string;
  challengeId?: string | null;
  challengeName?: string | null;
  points?: number | null;
  reason?: string | null;
  photoUrl?: string | null;
  createdAt: string;
}

type DisplayMode = 'leaderboard' | 'slideshow';

/**
 * Route element for /events/:id/display. A `?token=` display token (minted
 * via "Display screen" on the event detail page) authorizes the page on
 * its own — the API validates the token's event scope server-side — so
 * token-bearing visits skip the organization gate and work on signed-out
 * screens. Without a token, the route stays behind RequireOrganization.
 */
export function EventDisplayRoute() {
  const [searchParams] = useSearchParams();
  const hasDisplayToken = Boolean(searchParams.get(DISPLAY_TOKEN_PARAM));

  if (hasDisplayToken) {
    return <EventDisplayPage />;
  }

  return (
    <RequireOrganization>
      <EventDisplayPage />
    </RequireOrganization>
  );
}

/**
 * Big-screen display (TV / projector) for an event. Two modes —
 * leaderboard + live activity, and the photo slideshow — both styled
 * in the Destination Tours slide-deck language and themed by the
 * event's branding (background, accent, logo, points label). The deck
 * navy/pin-green look IS the default branding.
 */
export function EventDisplayPage() {
  const { id: eventId } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const apiClient = useApiClient();

  // Token mode — an event-scoped display token in `?token=` authorizes the
  // data endpoints and the socket handshake without a Clerk session (the
  // link is minted from the event detail page and opened on TVs).
  const displayToken = searchParams.get(DISPLAY_TOKEN_PARAM);

  const { isConnected, status: connectionStatus } = useDisplaySocket(
    eventId,
    displayToken ? { staticToken: displayToken } : undefined,
  );

  // Socket-first data flow: while connected, socket invalidations drive
  // updates and polling stretches to a slow backup sweep; disconnected,
  // polling falls back to fast intervals.
  const poll = displayPollIntervals(isConnected);

  // Tone — ?theme=light gives the deck's paper tone; default is the
  // navy stage (the deck cover look, built for dark rooms and TVs).
  const [tone, setTone] = useState<DisplayTone>(() =>
    searchParams.get('theme') === 'light' ? 'paper' : 'navy',
  );

  // DT-166. ?mode=slideshow swaps the leaderboard/activity layout
  // for the post-event photo cycler.
  const [mode, setMode] = useState<DisplayMode>(() =>
    searchParams.get('mode') === 'slideshow' ? 'slideshow' : 'leaderboard',
  );

  const updateSearchParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    next.set(key, value);
    setSearchParams(next);
  };

  const toggleTone = () => {
    const next = tone === 'paper' ? 'navy' : 'paper';
    setTone(next);
    updateSearchParam('theme', next === 'paper' ? 'light' : 'dark');
  };

  const switchMode = (next: DisplayMode) => {
    setMode(next);
    updateSearchParam('mode', next);
  };

  // Track new activity IDs for animation
  const [newActivityIds, setNewActivityIds] = useState<Set<string>>(new Set());
  const previousActivityIdsRef = useRef<Set<string>>(new Set());
  const isInitialLoadRef = useRef(true);

  const { data: eventData } = useQuery<{ success: boolean; data: DisplayEvent }>({
    queryKey: ['Event', eventId],
    queryFn: () =>
      apiClient.get(`/events/${eventId}`, { params: withDisplayToken(displayToken) }),
    enabled: !!eventId,
    staleTime: 30000,
  });

  // Teams for the leaderboard (polling fallback; totalScore is
  // computed so sorting happens client-side).
  const { data: teamsData } = useQuery<{ success: boolean; data: Team[] }>({
    queryKey: ['EventTeams', eventId],
    queryFn: () =>
      apiClient.get(`/events/${eventId}/teams`, { params: withDisplayToken(displayToken) }),
    enabled: !!eventId,
    refetchInterval: poll.teams,
    staleTime: 5000,
  });

  const { data: leaderboardData } = useQuery<{ success: boolean; data: LeaderboardData }>({
    queryKey: ['EventLeaderboard', eventId],
    queryFn: () =>
      apiClient.get(`/events/${eventId}/leaderboard`, {
        params: withDisplayToken(displayToken),
      }),
    enabled: !!eventId,
    refetchInterval: poll.leaderboard,
    staleTime: 5000,
  });

  const { data: activityData } = useQuery<{
    success: boolean;
    data: ActivityItem[];
  }>({
    queryKey: ['EventActivity', eventId],
    queryFn: () =>
      apiClient.get(`/events/${eventId}/activity`, {
        params: withDisplayToken(displayToken, { limit: 50 }),
      }),
    enabled: !!eventId,
    refetchInterval: poll.activity,
    staleTime: 5000,
  });

  // DT-166. Photos for the slideshow mode; fetched only while active.
  const { data: photosData } = useQuery<{
    success: boolean;
    data: {
      event: {
        id: string;
        name: string;
        logoUrl: string | null;
        backgroundColor: string | null;
        accentColor: string | null;
        slideshowIntervalSeconds: number;
      };
      photos: Array<{
        id: string;
        photoUrl: string;
        mediaType?: 'photo' | 'video';
        submittedAt: string;
        submittedBy: string | null;
        teamId: string;
        teamName: string;
        challengeId: string;
        challengeName: string;
      }>;
    };
  }>({
    queryKey: ['EventPhotos', eventId],
    queryFn: () =>
      apiClient.get(`/events/${eventId}/photos`, { params: withDisplayToken(displayToken) }),
    enabled: !!eventId && mode === 'slideshow',
    refetchInterval: poll.photos,
    staleTime: 15000,
  });

  const event = eventData?.data;
  const teams = useMemo(() => teamsData?.data || [], [teamsData]);
  const activities = useMemo(() => activityData?.data || [], [activityData]);
  const allianceEntries = leaderboardData?.data?.alliances || [];

  const branding = useMemo(
    () => ({
      backgroundColor: event?.backgroundColor,
      accentColor: event?.accentColor,
      logoUrl: event?.logoUrl,
      customPointsLabel: event?.customPointsLabel,
    }),
    [event],
  );
  const vars = useMemo(() => displayVars(branding, tone), [branding, tone]);
  const label = pointsLabel(branding);

  // Enrich activities with team icon info from the teams list
  const enrichedActivities = useMemo(() => {
    const teamMap = new Map(teams.map((t) => [t.id, t]));
    return activities.map((activity) => {
      const team = activity.teamId ? teamMap.get(activity.teamId) : null;
      return {
        ...activity,
        teamIconName: activity.teamIconName || team?.iconName,
        teamIconColor: activity.teamIconColor || team?.iconColor,
      };
    });
  }, [activities, teams]);

  // Track new activities for animation (skip initial load)
  useEffect(() => {
    const currentIds = new Set(activities.map((a) => a.id));
    if (isInitialLoadRef.current && activities.length > 0) {
      isInitialLoadRef.current = false;
      previousActivityIdsRef.current = currentIds;
      return;
    }
    const newIds = new Set<string>();
    currentIds.forEach((id) => {
      if (!previousActivityIdsRef.current.has(id)) newIds.add(id);
    });
    if (newIds.size > 0) {
      setNewActivityIds(newIds);
      const timer = setTimeout(() => setNewActivityIds(new Set()), 3000);
      previousActivityIdsRef.current = currentIds;
      return () => clearTimeout(timer);
    }
    previousActivityIdsRef.current = currentIds;
  }, [activities]);

  if (!eventId) {
    return (
      <div
        className="flex h-screen w-screen items-center justify-center"
        style={{ backgroundColor: '#1e293b' }}
      >
        <p className="font-mono text-sm uppercase tracking-[0.2em] text-white/50">
          Event not found
        </p>
      </div>
    );
  }

  const controlClasses =
    'flex items-center gap-2 rounded-full border px-4 py-1.5 font-mono text-[0.6875rem] font-medium uppercase tracking-[0.15em] transition-opacity hover:opacity-70';

  return (
    <>
      <style>{`
        @keyframes slowBlink {
          0%, 100% { opacity: 1; }
          25% { opacity: 0.4; }
          50% { opacity: 1; }
          75% { opacity: 0.4; }
        }
        @keyframes dspPinPulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.35); opacity: 0.35; }
        }
        @media (prefers-reduced-motion: reduce) {
          .dsp-animated { animation: none !important; }
        }
      `}</style>
      <div
        className="relative flex h-screen w-screen flex-col overflow-hidden"
        style={{ ...vars, backgroundColor: 'var(--dsp-bg)', color: 'var(--dsp-text)' }}
      >
        {/* City-grid texture (navy tone only, matches the deck) */}
        {tone === 'navy' && (
          <div aria-hidden className="pointer-events-none absolute inset-0" style={GRID_TEXTURE} />
        )}

        {/* Header */}
        <div
          className="relative z-10 flex flex-shrink-0 items-center justify-between border-b px-10 py-5"
          style={{ borderColor: 'var(--dsp-line)' }}
        >
          <div className="flex items-center gap-5">
            {branding.logoUrl ? (
              <img
                src={branding.logoUrl}
                alt=""
                className="h-11 w-auto object-contain"
              />
            ) : (
              <span className="relative flex h-3 w-3 items-center justify-center">
                <span
                  className="dsp-animated absolute h-3 w-3 rounded-full"
                  style={{
                    backgroundColor: 'var(--dsp-accent)',
                    animation: 'dspPinPulse 2.6s ease-in-out infinite',
                  }}
                />
                <span
                  className="relative h-2.5 w-2.5 rounded-full"
                  style={{ backgroundColor: 'var(--dsp-accent)' }}
                />
              </span>
            )}
            <div>
              <p
                className="font-mono text-[0.6875rem] font-medium uppercase tracking-[0.2em]"
                style={{ color: 'var(--dsp-accent-text)' }}
              >
                {mode === 'leaderboard' ? 'Live Leaderboard' : 'Photo Slideshow'}
              </p>
              <h1 className="text-3xl font-semibold tracking-tight">{event?.name || '…'}</h1>
            </div>
          </div>

          <div className="flex items-center gap-2.5" style={{ color: 'var(--dsp-soft)' }}>
            <button
              onClick={() => switchMode(mode === 'leaderboard' ? 'slideshow' : 'leaderboard')}
              className={controlClasses}
              style={{ borderColor: 'var(--dsp-line)' }}
            >
              {mode === 'leaderboard' ? (
                <>
                  <Camera className="h-3.5 w-3.5" /> Slideshow
                </>
              ) : (
                <>
                  <LayoutDashboard className="h-3.5 w-3.5" /> Leaderboard
                </>
              )}
            </button>
            <button
              onClick={toggleTone}
              className={controlClasses}
              style={{ borderColor: 'var(--dsp-line)' }}
              aria-label={tone === 'paper' ? 'Switch to dark stage' : 'Switch to light stage'}
            >
              {tone === 'paper' ? <Moon className="h-3.5 w-3.5" /> : <Sun className="h-3.5 w-3.5" />}
            </button>
          </div>
        </div>

        {/* Main content */}
        {mode === 'slideshow' && photosData?.data ? (
          <div className="relative z-10 min-h-0 flex-1">
            <DisplaySlideshow event={photosData.data.event} photos={photosData.data.photos} />
          </div>
        ) : mode === 'slideshow' ? (
          <div
            className="relative z-10 flex flex-1 items-center justify-center font-mono text-xs uppercase tracking-[0.2em]"
            style={{ color: 'var(--dsp-soft)' }}
          >
            Loading photos…
          </div>
        ) : (
          <div className="relative z-10 flex min-h-0 flex-1">
            <div
              className="w-[40%] flex-shrink-0 border-r"
              style={{ borderColor: 'var(--dsp-line)' }}
            >
              <DisplayLeaderboard
                teams={teams}
                alliances={allianceEntries}
                pointsLabel={label}
              />
            </div>
            <div className="flex-1">
              <DisplayActivityFeed
                activities={enrichedActivities}
                isConnected={isConnected}
                connectionStatus={connectionStatus}
                newActivityIds={newActivityIds}
                pointsLabel={label}
              />
            </div>
          </div>
        )}
      </div>
    </>
  );
}
